import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { RealtorEditDialog } from "./RealtorForms";

/**
 * RealtorPeekModal
 * ----------------
 * Opens a realtor's full edit dialog on top of whatever screen the user
 * is currently on (e.g. the Brokerage Details sheet) so they can peek
 * at contact info, log an outreach, or edit a note — then close the
 * dialog and land right back where they were, no navigation, no scroll
 * loss, no double-search.
 *
 * The parent supplies just the realtorId; this component handles the
 * fetch + the brokerages/locations bootstrap that RealtorEditDialog
 * expects. If any request fails, we surface a toast and close.
 *
 * Radix Dialogs stack correctly — this one will layer above any parent
 * Sheet/Dialog because of z-index inheritance.
 */
export default function RealtorPeekModal({ realtorId, onClose, onUpdated }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [realtor, setRealtor] = useState(null);
  const [brokerages, setBrokerages] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!realtorId) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [rRes, bRes, lRes] = await Promise.all([
          api.get(`/realtors/${realtorId}`),
          api.get("/brokerages"),
          isAdmin
            ? api.get("/locations").catch(() => ({ data: { locations: [] } }))
            : Promise.resolve({ data: { locations: [] } }),
        ]);
        if (cancelled) return;
        setRealtor(rRes.data);
        setBrokerages(bRes.data || []);
        setLocations(lRes.data?.locations || []);
      } catch (e) {
        if (!cancelled) {
          toast.error("Couldn't open realtor card", {
            description: formatApiError(e),
          });
          onClose && onClose();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [realtorId, isAdmin, onClose]);

  if (!realtorId) return null;
  if (loading || !realtor) {
    // Lightweight skeleton so the click feels responsive even on slow
    // networks. Rendered as a top-layer overlay so it doesn't shift the
    // Brokerage sheet underneath.
    return (
      <div
        className="fixed inset-0 z-[110] flex items-center justify-center bg-black/40 backdrop-blur-sm"
        role="dialog"
        aria-label="Loading realtor…"
        data-testid="realtor-peek-loading"
      >
        <div className="flex items-center gap-2 rounded-lg bg-white px-4 py-3 shadow-lg text-sm text-slate-700">
          <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
          Opening realtor card…
        </div>
      </div>
    );
  }

  return (
    <RealtorEditDialog
      realtor={realtor}
      brokerages={brokerages}
      locations={locations}
      isAdmin={isAdmin}
      onClose={onClose}
      onDone={(updated) => {
        // Bubble the fresh realtor back so the parent (e.g. Brokerage
        // detail) can refresh its cached names / avatars.
        if (updated && onUpdated) onUpdated(updated);
      }}
    />
  );
}
